import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Card, Grid, Typography } from '@mui/material';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';

// project imports
import { gridSpacing } from 'store/constant';

// assets
import { IconTallymark1 } from '@tabler/icons';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
import HomeIcon from '@mui/icons-material/Home';
import HomeTwoToneIcon from '@mui/icons-material/HomeTwoTone';

const linkSX = {
  display: 'flex',
  color: 'grey.900',
  textDecoration: 'none',
  alignContent: 'center',
  alignItems: 'center',
};

export default function Breadcrumbs({
  card,
  divider,
  icon,
  icons,
  maxItems,
  navigation,
  rightAlign,
  separator,
  title,
  titleBottom,
  ...others
}) {
  const theme = useTheme();
  const location = useLocation();

  const iconStyle = {
    marginRight: theme.spacing(0.75),
    marginTop: `-${theme.spacing(0.25)}`,
    width: '1rem',
    height: '1rem',
    color: theme.palette.secondary.main
  };

  const [main, setMain] = useState(null);
  const [item, setItem] = useState(null);
  const [subItem, setSubItem] = useState(null);
  const [baseMenu, setBaseMenu] = useState(null);

  const pathname = location.pathname;

  // Helper: find match in menu tree
  const findMatch = (menuGroup) => {
    // menuGroup.children can contain collapse/item
    const stack = [...(menuGroup.children || [])];

    while (stack.length) {
      const node = stack.shift();

      if (!node) continue;

      if (node.type === 'collapse' && node.children?.length) {
        // keep searching deeper
        stack.unshift(...node.children);
        continue;
      }

      if (node.type === 'item') {
        // old logic uses "includes"
        if (node.url && pathname.includes(node.url)) {
          // match sub item
          let matchedSub = null;

          if (node.children?.length) {
            for (const child of node.children) {
              if (!child?.url) continue;

              // exact child path match style
              if (pathname.includes(node.url + child.url)) matchedSub = child;

              // special /:id match
              if (child.url === '/:id' && pathname.includes(node.url + '/')) matchedSub = child;
            }
          }

          return { main: menuGroup, item: node, subItem: matchedSub };
        }
      }
    }

    return null;
  };

  // ✅ Run only when route or navigation changes
  useEffect(() => {
    // reset first
    setMain(null);
    setItem(null);
    setSubItem(null);
    setBaseMenu(null);

    const groups = navigation?.items || [];
    for (const menu of groups) {
      if (menu?.type !== 'group') continue;

      setBaseMenu(menu);

      const match = findMatch(menu);
      if (match) {
        setMain(match.main);
        setItem(match.item);
        setSubItem(match.subItem);
        break;
      }
    }
  }, [pathname, navigation]); // ✅ correct deps

  // separator
  const SeparatorIcon = separator;
  const separatorIcon = separator
    ? <SeparatorIcon stroke={1.5} size="1rem" />
    : <IconTallymark1 stroke={1.5} size="1rem" />;

  // build crumbs
  let baseContent;
  let mainContent;
  let itemContent;
  let subItemContent;
  let breadcrumbContent = <Typography />;

  const currUrl = window.location.href.split('/');

  // main/collapse label
  if (main && (main.type === 'group' || main.type === 'collapse') && main.title !== '') {
    const CollapseIcon = main.icon ? main.icon : AccountTreeTwoToneIcon;
    mainContent = (
      <Typography component={Link} to="#" variant="subtitle1" sx={linkSX}>
        {icons && <CollapseIcon style={iconStyle} />}
        {main.title}
      </Typography>
    );
  }

  // item label
  if (item && item.type === 'item' && item.title !== '') {
    const itemTitle = item.title;

    itemContent = (
      <Typography
        variant="subtitle1"
        sx={{
          display: 'flex',
          textDecoration: 'none',
          alignContent: 'center',
          alignItems: 'center',
          color: subItem ? 'grey.900' : 'grey.500',
          cursor: 'pointer'
        }}
        onClick={() => {
          window.location.href = document.location.origin + item.url;
        }}
      >
        {itemTitle === 'Admins' ? 'Admin' : itemTitle}
      </Typography>
    );

    if (subItem) {
      subItemContent = (
        <Typography
          variant="subtitle1"
          sx={{
            display: 'flex',
            textDecoration: 'none',
            alignContent: 'center',
            alignItems: 'center',
            color: 'grey.500',
          }}
        >
          {subItem.title}
        </Typography>
      );
    }

    if (item.breadcrumbs !== false) {
      breadcrumbContent = (
        <Card
          sx={{
            marginBottom: card === false ? 0 : theme.spacing(gridSpacing),
            border: card === false ? 'none' : '1px solid',
            borderColor: theme.palette.primary[200] + 75,
            background: card === false ? 'transparent' : theme.palette.background.default,
          }}
          {...others}
        >
          <Box sx={{ p: 2, pl: card === false ? 0 : 2, pt: card === false ? 0 : 2 }}>
            <Grid
              container
              direction={rightAlign ? 'row' : 'column'}
              justifyContent={rightAlign ? 'space-between' : 'flex-start'}
              alignItems={rightAlign ? 'center' : 'flex-start'}
              spacing={1}
            >
              {title && !titleBottom && (
                <Grid item>
                  <Typography variant="h3" sx={{ fontWeight: 500 }}>
                    {subItem ? `${subItem.title} ${item.title}` : item.title}
                  </Typography>
                </Grid>
              )}

              <Grid item>
                <MuiBreadcrumbs
                  sx={{ '& .MuiBreadcrumbs-separator': { width: 16, ml: 1.25, mr: 1.25 } }}
                  aria-label="breadcrumb"
                  maxItems={maxItems || 8}
                  separator={separatorIcon}
                >
                  <Typography component={Link} to={`/${currUrl[3]}`} color="inherit" variant="subtitle1" sx={linkSX}>
                    {icons && <HomeTwoToneIcon sx={iconStyle} />}
                    {icon && <HomeIcon sx={{ ...iconStyle, mr: 0 }} />}
                    {!icon && 'Dashboard'}
                  </Typography>

                  {baseContent}
                  {mainContent}
                  {itemContent}
                  {subItemContent}
                </MuiBreadcrumbs>
              </Grid>

              {title && titleBottom && (
                <Grid item>
                  <Typography variant="h3" sx={{ fontWeight: 500 }}>
                    {item.title}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </Card>
      );
    }
  }

  return breadcrumbContent;
}

Breadcrumbs.propTypes = {
  card: PropTypes.bool,
  divider: PropTypes.bool,
  icon: PropTypes.bool,
  icons: PropTypes.bool,
  maxItems: PropTypes.number,
  navigation: PropTypes.object,
  rightAlign: PropTypes.bool,
  separator: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  title: PropTypes.bool,
  titleBottom: PropTypes.bool
};